<template>
  <div class="jps-zooming-bg">
    <div class="jps-zooming-container" role="group" aria-roledescription="carousel">
      <BaseLink :to="to">
        <div class="jps-zooming-bg-img">
          <transition name="zooming-bg-fade">
            <div
              v-if="currentImage"
              :key="currentImage"
              class="jps-zooming-bg-img-body"
              :class="translate"
              :style="style"
              aria-roledescription="slide"
            ></div>
          </transition>
        </div>
      </BaseLink>
    </div>
    <div class="jps-zooming-ctrl-container">
      <div class="jps-zooming-ctrl" aria-label="スライドショー操作パネル">
        <BaseIcon 
          name="mdi/arrow-left-circle-outline"
          @click.stop="previous()"
          class="jps-zooming-bg-icon jps-zooming-bg-prev"
          :alt="ls(dapLangGlobal.slideShow.previous)"
        />
        <BaseIcon 
          :name="paused ? 'mdi/play-circle-outline' : 'mdi/stop-circle-outline'"
          @click.stop="toggle()"
          class="jps-zooming-bg-icon jps-zooming-bg-stop"
          :alt="paused ?　ls(dapLangGlobal.slideShow.resume) : ls(dapLangGlobal.slideShow.pause)"
        />
        <BaseIcon 
          name="mdi/arrow-right-circle-outline"
          @click.stop="next()"
          class="jps-zooming-bg-icon jps-zooming-bg-next"
          :alt="ls(dapLangGlobal.slideShow.next)"
        />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { dapLangGlobal } from '#imports'
const { ls } = useI18n()

type Props = {
  images: string[]
  to?: any
}

const {
  images,
} = defineProps<Props>()

const counter = ref(0)
const duration = 13000
const timeOutId = ref(null)
const startTime = ref(0)
const paused = ref(false)
const remaining = ref(0)

const translate = computed(() => {
  const index = ((counter.value + Math.floor(Math.random() * 9)) % 9) + 1
  return { ['t' + index]: true }
})

const style = computed(() => {
  return {
    backgroundImage: 'url(' + currentImage.value + ')',
    animationPlayState: paused.value ? 'paused' : 'running',
  }
})

const currentImageIndex = computed(() => {
  return counter.value % images.length
})

const currentImage = computed(() => {
  return images[currentImageIndex.value] || false
})

const emit = defineEmits(['change'])

watch(currentImageIndex,
  (index) => {
    emit('change', index)
    setTimeout(() => {
      const next = (index + 1) % images.length
      const img = new Image()
      img.src = images[next]
    }, duration / 2)
  },
  { immediate: true }
)


onMounted(() => {
  const img = new Image()
  img.onload = next
  img.src = images[0]
  startTime.value = Date.now()
  timeOutId.value = setTimeout(next, duration)
})

onUnmounted(() => {
  clearTimeout(timeOutId.value)
})

const next = () => {
  counter.value++
  paused.value = false
  startTime.value = Date.now()
  clearTimeout(timeOutId.value)
  timeOutId.value = setTimeout(next, duration)
}

const previous = () => {
  counter.value--
  counter.value += counter.value < 0 ? images.length : 0
  paused.value = false
  startTime.value = Date.now()
  clearTimeout(timeOutId.value)
  timeOutId.value = setTimeout(next, duration)
}

const pause = () => {
  paused.value = true
  clearTimeout(timeOutId.value)
  remaining.value = duration - (Date.now() - startTime.value)
  console.log('pushed')
}

const resume = () => {
  paused.value = false
  startTime.value = Date.now() - (duration - remaining.value)
  clearTimeout(timeOutId.value)
  timeOutId.value = setTimeout(next, remaining.value)
}

const toggle = () => {
  paused.value ? resume() : pause()
}
</script>

<style lang="scss" scoped>
@use '@/assets/styles/scss/vue' as *;

.jps-zooming-bg {
  .jps-zooming-bg-stop {
    z-index: 300;
  }
  .jps-zooming-container {
    display: block;
    overflow: hidden !important;
    position: relative;
    background: #2b2b2b;
    height: 40.2777%;
    min-height: 23.75rem;
    max-height: 36.25rem;
    .jps-zooming-bg-img {
      position: absolute;
      top: 0;
      left: 0;
      bottom: 0;
      right: 0;
      overflow: hidden;
      border: none;
      padding: 0;
      margin: 0;
      background: transparent center center no-repeat;
      transform: translateZ(0);
      z-index: 50;
      .jps-zooming-bg-img-body {
        position: absolute;
        top: 0;
        left: 0;
        bottom: 0;
        right: 0;
        overflow: hidden;
        border: none;
        padding: 0;
        margin: 0;
        background: transparent center center no-repeat;
        transform-origin: 50% 50%;
        background-position: center center;
        background-size: cover;
        background-color: rgb(0, 0, 0);
        transform: translateZ(0);
        will-change: transform, opacity;
      }
    }
  }
  .jps-zooming-ctrl-container {
    padding: 1.5rem 1rem 0.5rem;
    display: flex;
    justify-content: center;
    background-color: $hina-background-cream;
    border-bottom: 1px solid $hina-separator-dark;
    @include is-tablet {
      padding: 1rem 3rem;
      justify-content: flex-start;
    }
  }

  .jps-zooming-ctrl {
    right: 0;
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 6rem;
    > .jps-zooming-bg-icon {
      color: $jps-key-blue;
      cursor: pointer;
    }
  }

  .zooming-bg-fade-enter-from,
  .zooming-bg-fade-leave-to {
    opacity: 0;
  }
  .zooming-bg-fade-enter-active,
  .zooming-bg-fade-leave-active {
    transition: opacity 2s linear;
  }

  $duration: 15s; /* スライドショーの間隔＋フェイドイン／アウトのトランジンション */
  .jps-zooming-bg-img-body {
    &.t1 {
      animation: t1 $duration linear forwards;
    }
    &.t2 {
      animation: t2 $duration linear forwards;
    }
    &.t3 {
      animation: t3 $duration linear forwards;
    }
    &.t4 {
      animation: t4 $duration linear forwards;
    }
    &.t5 {
      animation: t5 $duration linear forwards;
    }
    &.t6 {
      animation: t6 $duration linear forwards;
    }
    &.t7 {
      animation: t7 $duration linear forwards;
    }
    &.t8 {
      animation: t8 $duration linear forwards;
    }
    &.t9 {
      animation: t9 $duration linear forwards;
    }
  }
  $scale: 1.2;
  $positive: 4%;
  $negative: -4%;
  @keyframes t1 {
    0% {
      transform: scale($scale) translate($negative, $negative);
    }
    100% {
      transform: scale($scale) translate(0, 0);
    }
  }
  @keyframes t2 {
    0% {
      transform: scale($scale) translate($negative, 0);
    }
    100% {
      transform: scale($scale) translate(0, 0);
    }
  }
  @keyframes t3 {
    0% {
      transform: scale($scale) translate($negative, $positive);
    }
    100% {
      transform: scale($scale) translate(0, 0);
    }
  }
  @keyframes t4 {
    0% {
      transform: scale($scale) translate(0%, $negative);
    }
    100% {
      transform: scale($scale) translate(0, 0);
    }
  }
  @keyframes t5 {
    0% {
      transform: scale($scale) translate(0%, 0%);
    }
    100% {
      transform: scale($scale) translate(0, 0);
    }
  }
  @keyframes t6 {
    0% {
      transform: scale($scale) translate(0%, $positive);
    }
    100% {
      transform: scale($scale) translate(0, 0);
    }
  }
  @keyframes t7 {
    0% {
      transform: scale($scale) translate($positive, $negative);
    }
    100% {
      transform: scale($scale) translate(0, 0);
    }
  }
  @keyframes t8 {
    0% {
      transform: scale($scale) translate($positive, 0);
    }
    100% {
      transform: scale($scale) translate(0, 0);
    }
  }
  @keyframes t9 {
    0% {
      transform: scale($scale) translate($positive, $positive);
    }
    100% {
      transform: scale($scale) translate(0, 0);
    }
  }
}
</style>